import React, { useState, useEffect, useMemo } from "react";
import { Button, message } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import Papa from "papaparse";
import { uploadListAPI } from "src/services/EngagementAPIs";
import CampaignTable from "src/components/reusable/CampaignTable/CampaignTable";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import { useTranslation } from "react-i18next";

interface ColumnData {
  column: string;
  type: string;
  sample: string;
  dataType: string;
}

interface LocationState {
  listName: string;
  tags: string[];
  csvFile: File;
  columnsData: ColumnData[];
  memberCount: number;
}

const ImportContacts: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const propertyOptions = [
    {
      value: "First Name",
      label: t("audienceOverview.importColumns.propertyOptions.firstName"),
    },
    {
      value: "Last Name",
      label: t("audienceOverview.importColumns.propertyOptions.lastName"),
    },
    {
      value: "Email Address",
      label: t("audienceOverview.importColumns.propertyOptions.emailAddress"),
    },
    {
      value: "Phone Number",
      label: t("audienceOverview.importColumns.propertyOptions.phoneNumber"),
    },
    {
      value: "unmapped",
      label: t("audienceOverview.importColumns.propertyOptions.unmapped"),
    },
  ];

  const [csvData, setCsvData] = useState<ColumnData[]>([]);
  const [listName, setListName] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [memberCount, setMemberCount] = useState<number>(0);
  const [modifiedColumnNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const state = location.state as LocationState;
    if (state) {
      setCsvData(state.columnsData);
      setListName(state.listName);
      setTags(state.tags);
      setCsvFile(state.csvFile);
      setMemberCount(state.memberCount);
    } else {
      navigate("/account/engagement/create-list");
    }
  }, [location, navigate]);

  const handlePropertyTypeChange = (value: string, column: string) => {
    setColumnMappings(prev => ({
      ...prev,
      [column]: value,
    }));
  };

  const processCSV = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        complete: results => {
          try {
            const headers = results.data[0] as string[];
            const rows = results.data.slice(1) as string[][];

            const selectedColumns = Object.entries(columnMappings)
              .filter(([, type]) => type !== "unmapped")
              .map(([originalName]) => ({
                originalName,
                newName: modifiedColumnNames[originalName] || originalName,
              }));

            const newHeaders = selectedColumns.map(col => col.newName);
            const newRows = rows.map(row =>
              selectedColumns.map(
                col => row[headers.indexOf(col.originalName)]
              )
            );

            const newCSV = Papa.unparse({
              fields: newHeaders,
              data: newRows,
            });

            const newFile = new File([newCSV], file.name, { type: "text/csv" });
            resolve(newFile);
          } catch (error) {
            reject(error);
          }
        },
        header: false,
        skipEmptyLines: true,
      });
    });
  };

  const validateMappings = (): boolean => {
    const mappedTypes = Object.values(columnMappings);
    const hasFirstName = mappedTypes.includes("First Name");
    const hasEmail = mappedTypes.includes("Email Address");

    if (!hasFirstName || !hasEmail) {
      const missingFields: string[] = [];
      if (!hasFirstName) {
        missingFields.push(t("audienceOverview.importColumns.firstNameMissing"));
      }
      if (!hasEmail) {
        missingFields.push(t("audienceOverview.importColumns.emailMissing"));
      }

      message.error(missingFields.join(" & "));
      return false;
    }

    return true;
  };

  const handleNext = async () => {
    try {
      setLoading(true);

      if (!validateMappings()) {
        setLoading(false);
        return;
      }

      if (!csvFile) {
        message.error(t("audienceOverview.importColumns.noCsvFile"));
        setLoading(false);
        return;
      }

      const tenant_id = localStorage.getItem("tenant_id");
      const user_id = localStorage.getItem("user_id");

      if (!tenant_id || !user_id) {
        message.error(t("audienceOverview.importColumns.missingTenantOrUser"));
        setLoading(false);
        return;
      }

      const processedFile = await processCSV(csvFile);

      const formData = new FormData();
      formData.append("tenant_id", tenant_id);
      formData.append("user_id", user_id);
      formData.append("list_name", listName);
      formData.append("list_type", "list");
      formData.append("members", memberCount.toString());

      Object.entries(columnMappings).forEach(([column, type]) => {
        if (type === "First Name") {
          formData.append("first_name", modifiedColumnNames[column] || column);
        } else if (type === "Last Name") {
          formData.append("last_name", modifiedColumnNames[column] || column);
        } else if (type === "Email Address") {
          formData.append("email_address", modifiedColumnNames[column] || column);
        } else if (type === "Phone Number") {
          formData.append("phone_number", modifiedColumnNames[column] || column);
        }
      });

      if (tags.length > 0) {
        tags.forEach(tag => formData.append("tags", tag));
      }

      formData.append("file", processedFile);

      const data = await uploadListAPI(formData);

      if (data.status === "success") {
        message.success(t("audienceOverview.importColumns.uploadSuccess"));
        navigate("/account/engagement/audience");
      } else {
        throw new Error(
          data.message || t("audienceOverview.importColumns.uploadFailed")
        );
      }
    } catch (error) {
      message.error(
        `${t("audienceOverview.importColumns.uploadFailedPrefix")} ${
          (error as Error).message
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: t("audienceOverview.importColumns.csvColumnTitle"),
        dataIndex: "column",
        key: "column",
        render: (value: string) => <span className="mapping-text">{value}</span>,
      },
      {
        title: t("audienceOverview.importColumns.sampleDataTitle"),
        dataIndex: "sample",
        key: "sample",
        render: (value: string) => (
          <span className="mapping-text">{value?.split(",")[0] || ""}</span>
        ),
      },
      {
        title: t("audienceOverview.importColumns.columnMappingTitle"),
        key: "mapping",
        render: (_: unknown, record: ColumnData) => (
          <StyledDropdown
            value={columnMappings[record.column]}
            onChange={value => handlePropertyTypeChange(value as string, record.column)}
            options={propertyOptions}
            placeholder={t("audienceOverview.importColumns.selectProperty")}
            className="import-mapping-dropdown"
            popupClassName="custom-dropdown-menu"
          />
        ),
      },
    ],
    [columnMappings, propertyOptions, t]
  );
  const mappedTypes = Object.values(columnMappings);
  const canUpload =
    mappedTypes.includes("First Name") && mappedTypes.includes("Email Address");

  return (
    <div className="import-contacts-container import-columns-page">
      <button
        className="button button--primary import-columns-back-button"
        onClick={() => navigate("/account/engagement/create-list")}
      >
        {t("audienceOverview.importColumns.back")}
      </button>

      <h1 className="import-columns-title">
        {t("audienceOverview.importColumns.title")}
      </h1>
      <p className="import-columns-subtitle">
        {t("audienceOverview.importColumns.subtitlePrefix")}
        <strong>
          {" "}
          {t("audienceOverview.importColumns.requiredEmailAndFirstName")}
        </strong>{" "}
        {t("audienceOverview.importColumns.subtitleSuffix")}
      </p>

      <div className="import-columns-card">
        <CampaignTable
          className="import-columns-table"
          columns={columns}
          data={csvData}
          rowKey="column"
          pagination={false}
        />
      </div>

      <div className="import-columns-actions">
        <Button
          type="primary"
          className="button button--primary"
          onClick={handleNext}
          loading={loading}
          disabled={!canUpload || loading}
        >
          {t("audienceOverview.importColumns.upload")}
        </Button>
      </div>
    </div>
  );
};

export default ImportContacts;
